
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { RestaurantSelectorBaseProps } from './types';

interface RestaurantDropdownSelectorProps extends RestaurantSelectorBaseProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  label?: string;
  showFullCard?: boolean;
  onMultiSelection: (restaurantId: string, closeAfterSelection?: boolean) => void;
  getDisplayValue: () => string;
}

export function RestaurantDropdownSelector({
  restaurants,
  selectedRestaurants,
  onRestaurantChange,
  isOpen,
  setIsOpen,
  label = 'Restaurants:',
  showFullCard = false,
  onMultiSelection,
  getDisplayValue,
  className
}: RestaurantDropdownSelectorProps) {
  if (showFullCard) {
    return (
      <Card className={cn("w-full", className)}>
        <CardContent className="p-4">
          <DropdownContent 
            restaurants={restaurants}
            selectedRestaurants={selectedRestaurants}
            onRestaurantChange={onRestaurantChange}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            label={label}
            onMultiSelection={onMultiSelection}
            getDisplayValue={getDisplayValue}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("mt-4", className)}>
      <DropdownContent 
        restaurants={restaurants}
        selectedRestaurants={selectedRestaurants}
        onRestaurantChange={onRestaurantChange}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        label={label}
        onMultiSelection={onMultiSelection}
        getDisplayValue={getDisplayValue}
      />
    </div>
  );
}

interface DropdownContentProps extends Omit<RestaurantDropdownSelectorProps, 'showFullCard' | 'className'> {}

function DropdownContent({
  restaurants,
  selectedRestaurants,
  onRestaurantChange,
  isOpen, 
  setIsOpen,
  label,
  onMultiSelection,
  getDisplayValue
}: DropdownContentProps) {
  const handleItemClick = (restaurantId: string) => {
    onMultiSelection(restaurantId, false);
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <label className="text-sm font-medium">
        {label}
      </label>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="justify-between min-w-48">
            {getDisplayValue()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0" align="start">
          <div className="py-2">
            <div className="px-2 py-1.5 text-sm font-semibold">Select Restaurants</div>
            <div className="-mx-1 my-1 h-px bg-muted"></div>
            <div 
              className="relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={() => handleItemClick('all')}
            >
              <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                {selectedRestaurants.includes('all') && (
                  <span className="h-4 w-4">✓</span>
                )}
              </span>
              All Restaurants
            </div>
            <div className="-mx-1 my-1 h-px bg-muted"></div>
            {restaurants.map(restaurant => (
              <div
                key={restaurant.id}
                className="relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                onClick={() => handleItemClick(restaurant.id)}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {selectedRestaurants.includes(restaurant.id) && (
                    <span className="h-4 w-4">✓</span>
                  )}
                </span>
                {restaurant.name}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
